import React from 'react';

import { Translation, translations, useTranslation } from 'src/shared/translation';
import { useAppConfig } from 'src/shared/contexts/app-config';
import { SelectInput, SelectInputProps } from 'src/shared/components/form-elements/select-input';
import { OptionalExceptFor } from 'src/types/optional-types';

const transl = translations.nearmecheck.start_page.form;

type Countries = ReturnType<typeof useAppConfig>['countries'];
type CountryId = keyof Countries;
type Country = NonNullable<Countries[CountryId]>;

type CountrySelectProps = {
    shouldUseSignUpCountries?: boolean;
} & OptionalExceptFor<SelectInputProps<Option>, 'onChange' | 'errorMessage' | 'id'> & {
        selectedValue?: CountryId;
    };
type Option = {
    value: CountryId;
    label: string;
    alt: string;
};

export const CountrySelect = (props: CountrySelectProps) => {
    const label = props.label || <Translation code={transl.country.label} />;
    const placeholderCode = props.placeholderCode || transl.country.placeholder;
    const noOptionsCode = props.noOptionsCode || transl.select_input.no_options;

    const { t } = useTranslation();
    const { countries, signupCountries } = useAppConfig();

    const countryEntries = props.shouldUseSignUpCountries
        ? (Object.entries(signupCountries) as [CountryId, Country][])
        : (Object.entries(countries) as [CountryId, Country][]);

    const countryOptions = countryEntries
        .map(([countryId, country]) => ({
            value: countryId,
            label: t(translations.country.name[countryId.toLowerCase()]),
            alt: country.alternativeName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const filterCountries = (option: { data: Option }, filterStr: string) => {
        const { value, label, alt } = option.data;
        const searchTerm = filterStr.toUpperCase();

        const valueMatch = value.toUpperCase().indexOf(searchTerm) > -1;
        const labelMatch = label.toUpperCase().indexOf(searchTerm) > -1;
        const altMatch = alt.toUpperCase().indexOf(searchTerm) > -1;

        return valueMatch || labelMatch || altMatch;
    };

    return (
        <div>
            <SelectInput
                label={label}
                placeholderCode={placeholderCode}
                noOptionsCode={noOptionsCode}
                options={countryOptions}
                filterOption={filterCountries}
                value={countryOptions.find((option) => option.value === props.selectedValue)}
                {...props}
            />
        </div>
    );
};
